import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import creditService from '../../services/creditService';
import { useI18n } from '../../contexts/I18nContext';

const MyCredits = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [creditInfo, setCreditInfo] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const pageSize = 10;

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async (page = 0) => {
    try {
      setLoading(true);
      
      const [creditData, transactionData] = await Promise.all([
        creditService.getUserCredits(),
        creditService.getCreditTransactions(page, pageSize)
      ]);
      
      setCreditInfo(creditData);
      
      const newTransactions = transactionData.content || transactionData;
      setTransactions(newTransactions);
      
      // 设置分页信息
      if (transactionData.totalPages !== undefined) {
        setTotalPages(transactionData.totalPages);
      } else {
        // 如果没有分页信息，根据数据长度估算
        setTotalPages(newTransactions.length < pageSize ? page + 1 : page + 2);
      }
      
      setCurrentPage(page);
    } catch (err) {
      setError('Failed to load data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // 上一页
  const handlePreviousPage = () => {
    if (currentPage > 0) {
      loadData(currentPage - 1);
    }
  };

  // 下一页
  const handleNextPage = () => {
    if (currentPage < totalPages - 1) {
      loadData(currentPage + 1);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getTransactionTypeText = (type) => {
    const typeMap = {
      'EARNED': t('credits.transactionType.earned'),
      'SPENT': t('credits.transactionType.spent'),
      'REFUNDED': t('credits.transactionType.refunded'),
      'Catoo_generate': t('credits.transactionType.catooGenerate'),
      'new_user': t('credits.transactionType.newUserReward')
    };
    return typeMap[type] || type;
  };

  // 根据交易类型判断积分变化的正负
  // const getAmountDisplay = (transaction) => {
  //   const amount = transaction.amount || 0;
  //   const type = transaction.transactionType || transaction.type;
    
  //   // SPENT类型显示为负数，其他类型显示为正数
  //   if (type === 'SPENT') {
  //     return `-${amount}`;
  //   } else {
  //     return `+${amount}`;
  //   }
  // };

  // 根据交易类型获取CSS类名
  const getAmountClassName = (transaction) => {
    const type = transaction.transactionType || transaction.type;
    return type === 'SPENT' ? 'credit-negative' : 'credit-positive';
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>{t('common.loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <p className="error-message">{error}</p>
        <button onClick={loadData} className="retry-button">
          {t('common.retry')}
        </button>
      </div>
    );
  }

  return (
    <div className="my-credits">
      {/* Page Title */}
      <div className="page-header">
        <h1>{t('personalCenter.myCredits')}</h1>
      </div>

      {/* Credit Balance Card */}
      <div className="card">
        <div className="card-header">
          <h3>{t('credits.remainingCredits')}</h3>
          <button 
            onClick={() => navigate('/pricing')}
            className="btn btn-primary"
          >
            <Plus size={16} />
            {t('credits.recharge')}
          </button>
        </div>
        <div className="card-content">
          <div className="reward-balance">
            <div className="balance-amount">
              {creditInfo?.totalCredits || 0}
            </div>
          </div>
        </div>
      </div>

      {/* Transaction History */}
      <div className="table-container">
        <div className="profile-table-header">
          <h3>{t('credits.transactionHistory')}</h3>
          <div className="pagination-controls" style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
            <button 
              onClick={handlePreviousPage}
              disabled={currentPage === 0}
              className="pagination-btn"
              style={{
                border: '1px solid #ddd',
                borderRadius: '4px',
                background: currentPage === 0 ? '#f5f5f5' : '#fff',
                cursor: currentPage === 0 ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <ChevronLeft size={16} />
            </button>
            <span style={{fontSize: '14px', color: '#666'}}>
              {currentPage + 1} / {totalPages || 1}
            </span>
            <button 
              onClick={handleNextPage}
              disabled={currentPage >= totalPages - 1}
              className="pagination-btn"
              style={{
                border: '1px solid #ddd',
                borderRadius: '4px',
                background: currentPage >= totalPages - 1 ? '#f5f5f5' : '#fff',
                cursor: currentPage >= totalPages - 1 ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
        
        <div className="table-wrapper">
          <table className="table">
            <thead>
              <tr>
                <th>{t('credits.transactionNo')}</th>
                <th>{t('credits.transactionType.title')}</th>
                <th>{t('credits.credits')}</th>
                <th>{t('credits.remainingCredits')}</th>
                <th>{t('credits.transactionTime')}</th>
              </tr>
            </thead>
            <tbody>
              {transactions.length > 0 ? (
                transactions.map((transaction, index) => (
                  <tr key={transaction.id || index}>
                    <td>{transaction.publicId || transaction.id || '-'}</td>
                    <td>
                      <span className={`transaction-type ${transaction.type?.toLowerCase()}`}>
                        {getTransactionTypeText(transaction.type)}
                      </span>
                    </td>
                    <td>
                      <span className={`amount ${getAmountClassName(transaction)}`}>
                        {/* {getAmountDisplay(transaction)} */}
                        { transaction.amount }
                      </span>
                    </td>
                    <td>{transaction.credits}</td>
                    <td>{formatDate(transaction.createdAt || transaction.transactionTime)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="no-data">
                    {t('credits.noTransactionRecords')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MyCredits;
